// File: frontend/src/layouts/LeftPane.tsx
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./LeftPane.css";
import { MatchModel } from "../models";
import { PersonModel } from "../models/PersonModel";
import { ClubModel } from "../models/ClubModel";
import { TeamModel } from "../models/TeamModel";
import { Pagination } from "../utils";
import { MatchList } from "../components/matches";
import { PersonList } from "../components/people";
import { ClubList } from "../components/clubs";
import { TeamList } from "../components/teams";
import { CustomTopicList, SuggestCustomTopicModal } from "../components/customTopics";
// Sitewide admin pins: hoists the pinned topic of each domain to the top of its list
import { usePinnedFirst } from "../hooks/usePinnedFirst";
import { CustomTopicModel } from "../models/CustomTopicModel";
import { FaSyncAlt, FaFire, FaClock, FaPlus, FaRandom } from "react-icons/fa";
// Rookie check for the "+" (Başlık Öner) button — rookies cannot suggest topics
import { useAuth } from "../context/AuthContext";
import { PeopleSortOption } from "../hooks/usePeople";
import { ClubsSortOption } from "../hooks/useClubs";
import { TeamsSortOption } from "../hooks/useTeams";
import { CustomTopicsSortOption } from "../hooks/useCustomTopics";

const formatBadgeCount = (count: number): string => {
  if (count <= 0) return "";
  if (count <= 999) return String(count);
  const k = Math.floor(count / 1000);
  return `${k}k`;
};

type PaneMode = "matches" | "people" | "clubs" | "teams" | "topics" | "istikram" | "birak";

interface LeftPaneProps {
  // Controlled pane mode — lifted to App.tsx so it survives SpinnerLoading unmount/remount.
  // The setter now lives with the full-width DomainTabBar; the pane only reads the mode.
  paneMode: PaneMode;
  // Matches
  matches: MatchModel[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
  paginate: (page: number) => void;
  onSelectMatch: (matchId: number) => void;
  selectedMatchId: number | null;
  onRefresh?: () => void;
  sortBy: "lastEntryAt" | "recentEntryCount";
  setSortBy: (sort: "lastEntryAt" | "recentEntryCount") => void;
  newEntryCount?: number;
  loadMoreMatches?: () => Promise<void>;
  isLoadingMore?: boolean;
  // People
  people: PersonModel[];
  selectedPersonId: number | null;
  onSelectPerson: (personId: number) => void;
  onRefreshPeople?: () => void;
  loadMorePeople?: () => Promise<void>;
  isLoadingMorePeople?: boolean;
  peopleTotalPages?: number;
  peopleCurrentPage?: number;
  peopleSortBy?: PeopleSortOption;
  onPeopleSortChange?: (sort: PeopleSortOption) => void;
  // Clubs
  clubs: ClubModel[];
  selectedClubId: number | null;
  onSelectClub: (clubId: number) => void;
  onRefreshClubs?: () => void;
  loadMoreClubs?: () => Promise<void>;
  isLoadingMoreClubs?: boolean;
  clubsTotalPages?: number;
  clubsCurrentPage?: number;
  clubsSortBy?: ClubsSortOption;
  onClubsSortChange?: (sort: ClubsSortOption) => void;
  // Teams
  teams: TeamModel[];
  selectedTeamId: number | null;
  onSelectTeam: (teamId: number) => void;
  onRefreshTeams?: () => void;
  loadMoreTeams?: () => Promise<void>;
  isLoadingMoreTeams?: boolean;
  teamsTotalPages?: number;
  teamsCurrentPage?: number;
  teamsSortBy?: TeamsSortOption;
  onTeamsSortChange?: (sort: TeamsSortOption) => void;
  // Custom topics ("Özel")
  topics: CustomTopicModel[];
  selectedTopicId: number | null;
  onSelectTopic: (topicId: number) => void;
  onRefreshTopics?: () => void;
  // Shuffle ("Rastgele") — replaces the topic list with a random slice of
  // custom topics. Only wired for the three custom-topic panes.
  onShuffleTopics?: () => Promise<void> | void;
  loadMoreTopics?: () => Promise<void>;
  isLoadingMoreTopics?: boolean;
  topicsTotalPages?: number;
  topicsCurrentPage?: number;
  topicsSortBy?: CustomTopicsSortOption;
  onTopicsSortChange?: (sort: CustomTopicsSortOption) => void;
  // Per-domain filters — moved out of the left pane entirely. PaneFilterBar
  // now renders inside the full-width DomainTabBar (see App.tsx); the left
  // pane keeps only Son / Bugün, the "+" (Başlık Öner) button and Yenile.
  // Called when a person/club/team row is tapped on mobile so App.tsx can
  // switch the visible pane from the list to the content area
  onMobileNavigate?: () => void;
}

export const LeftPane: React.FC<LeftPaneProps> = ({
  paneMode,
  matches, currentPage, totalPages, totalItems, paginate,
  onSelectMatch, selectedMatchId, onRefresh, sortBy, setSortBy,
  newEntryCount = 0, loadMoreMatches, isLoadingMore = false,
  people, selectedPersonId, onSelectPerson, onRefreshPeople,
  loadMorePeople, isLoadingMorePeople = false,
  peopleTotalPages = 1, peopleCurrentPage = 0,
  peopleSortBy = "recentEntryCount", onPeopleSortChange,
  clubs, selectedClubId, onSelectClub, onRefreshClubs,
  loadMoreClubs, isLoadingMoreClubs = false,
  clubsTotalPages = 1, clubsCurrentPage = 0,
  clubsSortBy = "recentEntryCount" as ClubsSortOption, onClubsSortChange,
  teams, selectedTeamId, onSelectTeam, onRefreshTeams,
  loadMoreTeams, isLoadingMoreTeams = false,
  teamsTotalPages = 1, teamsCurrentPage = 0,
  teamsSortBy = "recentEntryCount" as TeamsSortOption, onTeamsSortChange,
  topics, selectedTopicId, onSelectTopic, onRefreshTopics, onShuffleTopics,
  loadMoreTopics, isLoadingMoreTopics = false,
  topicsTotalPages = 1, topicsCurrentPage = 0,
  topicsSortBy = "recentEntryCount" as CustomTopicsSortOption, onTopicsSortChange,
  onMobileNavigate,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  // Rookies (çaylaklar) are not allowed to suggest topics — hide the "+" button
  const isRookie = user?.role?.toUpperCase() === "ROOKIE";
  const [spinning, setSpinning] = useState(false);
  // Short-lived flag that drives the shuffle button's spin animation
  const [shuffling, setShuffling] = useState(false);
  // Controls the "Başlık Öner" suggestion modal in the topics pane
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const scrollableRef = useRef<HTMLDivElement>(null);

  // Prevents the button from receiving focus on click so the browser won't
  // scroll the container to bring the focused button into view.
  const noFocus = (e: React.MouseEvent) => e.preventDefault();

  const handleRefresh = async () => {
    const refreshFn =
      paneMode === "matches" ? onRefresh :
      paneMode === "people"  ? onRefreshPeople :
      paneMode === "clubs"   ? onRefreshClubs :
      // "topics" | "istikram" | "birak" all share onRefreshTopics — App.tsx
      // points it at whichever domain's refresh function matches paneMode.
      paneMode === "topics" || paneMode === "istikram" || paneMode === "birak" ? onRefreshTopics :
      onRefreshTeams;
    if (!refreshFn) return;
    setSpinning(true);
    await refreshFn();
    setTimeout(() => setSpinning(false), 600);
  };

  // Shuffle ("Rastgele") — topic panes only. Mirrors handleRefresh's spin
  // feedback so the click is visibly acknowledged even when the request is fast.
  const handleShuffle = async () => {
    if (!onShuffleTopics) return;
    setShuffling(true);
    await onShuffleTopics();
    setTimeout(() => setShuffling(false), 600);
  };

  // Sitewide pinned topics — reorder each domain list so the pinned topic sits on top
  const { orderedItems: orderedMatches, pinnedTopicId: pinnedMatchId } = usePinnedFirst("MATCH", matches);
  const { orderedItems: orderedPeople,  pinnedTopicId: pinnedPersonId } = usePinnedFirst("PERSON", people);
  const { orderedItems: orderedClubs,   pinnedTopicId: pinnedClubId } = usePinnedFirst("CLUB", clubs);
  const { orderedItems: orderedTeams,   pinnedTopicId: pinnedTeamId } = usePinnedFirst("TEAM", teams);
  const { orderedItems: orderedTopics,  pinnedTopicId: pinnedCustomTopicId } = usePinnedFirst("CUSTOM_TOPIC", topics);

  const hasMoreMatches = currentPage < totalPages;
  const hasMorePeople  = peopleCurrentPage + 1 < peopleTotalPages;
  const hasMoreClubs   = clubsCurrentPage  + 1 < clubsTotalPages;
  const hasMoreTeams   = teamsCurrentPage  + 1 < teamsTotalPages;
  const hasMoreTopics  = topicsCurrentPage + 1 < topicsTotalPages;

  // Unified sort state — every domain exposes the same two options
  // ("Son" / "Bugün"), so a single toggle drives all seven panes and the
  // handler routes the change to the right domain callback. Behavior is
  // identical to the old five per-domain toggle blocks. İstikram/Bırak share
  // topicsSortBy/onTopicsSortChange with Gündem — App.tsx points those props
  // at whichever domain's hook matches the current paneMode.
  const currentSort: "lastEntryAt" | "recentEntryCount" =
    paneMode === "matches" ? sortBy :
    paneMode === "people"  ? peopleSortBy :
    paneMode === "clubs"   ? clubsSortBy :
    paneMode === "teams"   ? teamsSortBy :
    topicsSortBy;

  const handleSortChange = (sort: "lastEntryAt" | "recentEntryCount") => {
    if (paneMode === "matches") {
      // Matches keep the old no-op guard: re-clicking the active sort does nothing
      if (sortBy !== sort) setSortBy(sort);
    } else if (paneMode === "people") {
      onPeopleSortChange?.(sort);
    } else if (paneMode === "clubs") {
      onClubsSortChange?.(sort);
    } else if (paneMode === "teams") {
      onTeamsSortChange?.(sort);
    } else {
      // "topics" | "istikram" | "birak"
      onTopicsSortChange?.(sort);
    }
  };

  return (
    <aside className="left-pane">

      {/* ── Fixed header: one compact toolbar row ──
          The five-domain mode toggle and the filters both moved out of the
          left pane (DomainTabBar + PaneFilterBar at the top of the page).
          What remains here is a single row: Son | Bugün | + | Yenile. */}
      <div className="left-pane__header">

        {/* Sort + Yenile toolbar */}
        <div className="left-pane__refresh-bar">
          {/* Unified sort toggle — one control for all five domains */}
          <div className="left-pane__sort-toggle">
            <button
              onMouseDown={noFocus}
              className={`left-pane__sort-btn ${currentSort === "lastEntryAt" ? "active" : ""}`}
              onClick={() => handleSortChange("lastEntryAt")}
              title={t("sort_by_last_entry")}
            >
              <FaClock size={11} />
              <span>{t("sort_last_entry", "Son Entry")}</span>
            </button>
            <button
              onMouseDown={noFocus}
              className={`left-pane__sort-btn ${currentSort === "recentEntryCount" ? "active" : ""}`}
              onClick={() => handleSortChange("recentEntryCount")}
              title={t("sort_by_today_count")}
            >
              <FaFire size={11} />
              <span>{t("sort_today", "Bugün")}</span>
            </button>
          </div>

          {/* Right-aligned action buttons: "+" and Rastgele (topics only) and Yenile */}
          <div className="left-pane__toolbar-actions">
            {/* "+" (Başlık Öner) — topics/istikram/birak only, hidden for
                rookies. Opens the suggestion modal owned by this pane.
                Suggesting into İstikram or Bırak is the same flow as Gündem —
                the user just picks İstikram/Bırak as the topic's first
                channel in the modal; the backend routes it there instead of
                Gündem. */}
            {(paneMode === "topics" || paneMode === "istikram" || paneMode === "birak") && !isRookie && (
              <button
                onMouseDown={noFocus}
                className="left-pane__suggest-btn"
                onClick={() => setShowSuggestModal(true)}
                title={t("suggest_topic_button", "Başlık Öner")}
                aria-label={t("suggest_topic_button", "Başlık Öner")}
              >
                <FaPlus size={12} />
              </button>
            )}

            {/* Shuffle (Rastgele) — topics/istikram/birak only. Sits between
                "+" and Yenile and swaps the sorted list for a random slice of
                custom topics. Visible to every visitor, including rookies and
                guests: it only reads the public list. */}
            {(paneMode === "topics" || paneMode === "istikram" || paneMode === "birak") && onShuffleTopics && (
              <button
                onMouseDown={noFocus}
                className={`left-pane__shuffle-btn ${shuffling ? "spinning" : ""}`}
                onClick={handleShuffle}
                title={t("shuffle_topics_button", "Rastgele")}
                aria-label={t("shuffle_topics_button", "Rastgele")}
              >
                <FaRandom size={12} />
              </button>
            )}

            {/* Yenile — icon-only to keep the single row compact; the label
                lives in the tooltip / aria-label and the new-entry badge is
                unchanged. */}
            <button
              onMouseDown={noFocus}
              className={`left-pane__refresh-btn ${spinning ? "spinning" : ""} ${newEntryCount > 0 ? "has-new" : ""}`}
              onClick={handleRefresh}
              title={t("refresh_btn", "Yenile")}
              aria-label={t("refresh", "Yenile")}
            >
              <FaSyncAlt size={13} />
              {newEntryCount > 0 && (
                <span className="left-pane__refresh-badge">{formatBadgeCount(newEntryCount)}</span>
              )}
            </button>
          </div>
        </div>

      </div>{/* /left-pane__header */}

      {/* ── Scrollable content ── */}
      <div className="left-pane__scrollable" ref={scrollableRef}>

        {/* MATCHES */}
        {paneMode === "matches" && (
          <>
            <div className="pagination-top">
              <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} paginate={paginate} />
            </div>
            <div className="matches-container">
              <MatchList matches={orderedMatches} onSelectMatch={onSelectMatch} selectedMatchId={selectedMatchId} pinnedMatchId={pinnedMatchId} />
            </div>
            {hasMoreMatches && loadMoreMatches && (
              <button className={`left-pane__load-more ${isLoadingMore ? "loading" : ""}`} onClick={loadMoreMatches} disabled={isLoadingMore}>
                {isLoadingMore ? t("load_more_loading", "yükleniyor...") : t("load_more_matches", "daha da...")}
              </button>
            )}
            <div className="pagination-bottom">
              <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} paginate={paginate} />
            </div>
          </>
        )}

        {/* PEOPLE */}
        {paneMode === "people" && (
          <>
            <div className="matches-container">
              <PersonList
                people={orderedPeople}
                pinnedPersonId={pinnedPersonId}
                selectedPersonId={selectedPersonId}
                onSelectPerson={onSelectPerson}
                onMobileNavigate={onMobileNavigate}
              />
            </div>
            {hasMorePeople && loadMorePeople && (
              <button className={`left-pane__load-more ${isLoadingMorePeople ? "loading" : ""}`} onClick={loadMorePeople} disabled={isLoadingMorePeople}>
                {isLoadingMorePeople ? t("load_more_loading", "yükleniyor...") : t("load_more_people", "daha da...")}
              </button>
            )}
          </>
        )}

        {/* CLUBS */}
        {paneMode === "clubs" && (
          <>
            <div className="matches-container">
              <ClubList
                clubs={orderedClubs}
                pinnedClubId={pinnedClubId}
                selectedClubId={selectedClubId}
                onSelectClub={onSelectClub}
                onMobileNavigate={onMobileNavigate}
              />
            </div>
            {hasMoreClubs && loadMoreClubs && (
              <button className={`left-pane__load-more ${isLoadingMoreClubs ? "loading" : ""}`} onClick={loadMoreClubs} disabled={isLoadingMoreClubs}>
                {isLoadingMoreClubs ? t("load_more_loading", "yükleniyor...") : t("load_more_clubs", "daha da...")}
              </button>
            )}
          </>
        )}

        {/* TEAMS */}
        {paneMode === "teams" && (
          <>
            <div className="matches-container">
              <TeamList
                teams={orderedTeams}
                pinnedTeamId={pinnedTeamId}
                selectedTeamId={selectedTeamId}
                onSelectTeam={onSelectTeam}
                onMobileNavigate={onMobileNavigate}
              />
            </div>
            {hasMoreTeams && loadMoreTeams && (
              <button className={`left-pane__load-more ${isLoadingMoreTeams ? "loading" : ""}`} onClick={loadMoreTeams} disabled={isLoadingMoreTeams}>
                {isLoadingMoreTeams ? t("load_more_loading", "yükleniyor...") : t("load_more_teams", "daha da...")}
              </button>
            )}
          </>
        )}

        {/* CUSTOM TOPICS ("Özel") — Gündem, İstikram, and Bırak all render the
            same list component; App.tsx swaps which domain's data flows into
            the `topics`/`onSelectTopic`/etc. props above based on paneMode. */}
        {(paneMode === "topics" || paneMode === "istikram" || paneMode === "birak") && (
          <>
            <div className="matches-container">
              <CustomTopicList
                topics={orderedTopics}
                pinnedTopicId={pinnedCustomTopicId}
                selectedTopicId={selectedTopicId}
                onSelectTopic={onSelectTopic}
                onMobileNavigate={onMobileNavigate}
              />
            </div>
            {hasMoreTopics && loadMoreTopics && (
              <button className={`left-pane__load-more ${isLoadingMoreTopics ? "loading" : ""}`} onClick={loadMoreTopics} disabled={isLoadingMoreTopics}>
                {isLoadingMoreTopics ? t("load_more_loading", "yükleniyor...") : t("load_more_topics", "daha da...")}
              </button>
            )}
          </>
        )}

      </div>{/* /left-pane__scrollable */}

      {/* Suggest-topic modal — mounted at pane level so it overlays the whole app */}
      {showSuggestModal && (
        <SuggestCustomTopicModal onClose={() => setShowSuggestModal(false)} />
      )}

    </aside>
  );
};